/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useEffect, useMemo, useState } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import _ from 'underscore';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
// @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module 'js/l... Remove this comment to see the full error message
import provideFluxibleAppContext from 'js/lib/provideFluxibleAppContext';
// @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module 'js/l... Remove this comment to see the full error message
import setupFluxibleApp from 'js/lib/setupFluxibleApp';

import { Typography2 } from '@coursera/cds-core';

import { getClassmatesProfile } from 'bundles/classmates/actions/ClassmatesProfileActions';
import ProfileImageWithRole from 'bundles/classmates/components/ProfileImageWithRole';
import ClassmatesProfileStore from 'bundles/classmates/stores/ClassmatesProfileStore';
import {
  getCourseRoleString,
  getCourseRoleValue,
  getProgressString,
  hasModeratorAccess,
  isTopContributor,
} from 'bundles/classmates/utils/lib';
import Icon from 'bundles/iconfont/Icon';

import _t from 'i18n!nls/classmates';

type MiniProfileProps = {
  externalId: string;
  courseId?: string;
  courseRole?: string;
  orientationClass: string;
  profile: any;
  profileRequested?: boolean;
  toolTipId?: string;
  fullName: string;
  profileImageUrl?: string;
};

type MiniProfileLegacyContext = {
  executeAction: any;
};

export function getProfileDescription(courseRole: string, profile: any) {
  if (hasModeratorAccess(courseRole) || isTopContributor(courseRole)) {
    return getCourseRoleString(courseRole);
  }
  return getProgressString(profile.courseProgressPercentage, profile.isCourseCompleted);
}

const style = () => css`
  opacity: 0;

  &.transition-in {
    opacity: 1;
    transition: opacity 0.3s ease- in;
  }

  .profile-header {
    padding: var(--cds-spacing-200) var(--cds-spacing-150) var(--cds-spacing-150);
  }

  .profile-photo {
    margin-bottom: var(--cds-spacing-150);
  }

  .profile-footer {
    padding: var(--cds-spacing-150) var(--cds-spacing-200) var(--cds-spacing-200);
  }

  .unavailable-state {
    text-align: center;
    margin: var(--cds-spacing-600) var(--cds-spacing-200);
  }

  .loading-state {
    text-align: center;
    margin: var(--cds-spacing-600);
  }

  .dot-spacing {
    margin: 0 4px;
  }

  &.profileAreaNorth {
    margin-top: -205px;
  }

  &.profileAreaSouth {
    margin-top: 5px;
  }

  &.profileNameNorth {
    margin-top: -185px;
  }

  &.profileNameSouth {
    margin-top: 5px;
  }
`;

const MiniProfile = (
  {
    externalId,
    courseId,
    courseRole,
    orientationClass,
    profile,
    profileRequested,
    toolTipId,
    fullName,
    profileImageUrl,
  }: MiniProfileProps,
  { executeAction }: MiniProfileLegacyContext
) => {
  const [transitionContainer, setTransitionContainer] = useState(false);
  const hasLoaded = useMemo(() => {
    return profile && Object.keys(profile).length !== 0;
  }, [profile]);

  useEffect(() => {
    if (!hasLoaded) {
      executeAction(getClassmatesProfile, {
        externalId,
        courseId,
      });
    }
  }, [courseId, executeAction, externalId, hasLoaded, profile]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setTransitionContainer(true);
    }, 100);
    return () => clearTimeout(timeout);
  }, [setTransitionContainer]);

  function renderMiniProfile() {
    const courseRoleValue = getCourseRoleValue(
      profile.courseRole || courseRole,
      profile['forumHelpers.v1'] && profile['forumHelpers.v1'].helperStatus
    );
    const profileFooterClassnames = classNames(
      'profile-footer',
      'caption-text',
      'color-secondary-text',
      'bgcolor-black-g1',
      'horizontal-box',
      'align-items-absolute-center'
    );
    const fullNameResolved = profile.fullName || fullName;
    return (
      <Typography2 component="div" className="vertical-box" id={toolTipId} role="tooltip">
        <Typography2 component="div" className="vertical-box profile-header align-items-vertical-center">
          <div className="profile-photo">
            <ProfileImageWithRole
              courseRole={courseRoleValue}
              fullName={fullNameResolved}
              profileImageUrl={profile.photoUrl || profileImageUrl || ''}
            />
          </div>
          <Typography2 component="div" className="pii-hide">
            {fullNameResolved}
          </Typography2>
          <Typography2 component="div" className="caption-text" color="supportText">
            {getProfileDescription(courseRoleValue, profile)}
          </Typography2>
        </Typography2>
        <Typography2 component="div" className={profileFooterClassnames}>
          <FormattedMessage
            message={_t('{postsCount} {postsCount, plural, =1 { post} other { posts}}')}
            postsCount={profile.postsCount || '0'}
          />
          <span className="dot-spacing"> · </span>
          <FormattedMessage
            message={_t('{upvotes} {upvotes, plural, =1 { upvote} other { upvotes}}')}
            upvotes={profile.votesReceivedCount || '0'}
          />
        </Typography2>
      </Typography2>
    );
  }

  function renderProfileUnavailable() {
    if (profileRequested) {
      return (
        <Typography2 component="div" className="unavailable-state">
          <FormattedMessage message={_t('Profile not available')} />
        </Typography2>
      );
    }
    return (
      <div className="loading-state">
        <Icon name="spinner" size="3x" spin={true} />
      </div>
    );
  }

  const containerClasses = classNames('rc-MiniProfile', 'card-one-clicker', orientationClass, {
    'transition-in': transitionContainer,
  });

  return (
    <div className={containerClasses} css={style}>
      {hasLoaded ? renderMiniProfile() : renderProfileUnavailable()}
    </div>
  );
};

MiniProfile.contextTypes = { executeAction: PropTypes.func.isRequired };

export default _.compose(
  provideFluxibleAppContext((fluxibleContext: $TSFixMe) => {
    return setupFluxibleApp(fluxibleContext, (app: $TSFixMe) => {
      app.registerStore(ClassmatesProfileStore);

      return fluxibleContext;
    });
  }),
  connectToStores(
    ['CourseStore', 'ClassmatesProfileStore'],
    ({ CourseStore, ClassmatesProfileStore: myClassmatesProfileStore }, { externalId }: MiniProfileProps) => {
      return {
        courseId: CourseStore.getCourseId(),
        profile: _(myClassmatesProfileStore.profiles).findWhere({ externalId }),
        profileRequested: myClassmatesProfileStore.profilesRequested.includes(externalId),
      };
    }
  )
)(MiniProfile);
